import React from 'react';
import { Box, Flex, Text, Heading } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { Check, ExternalLink, List } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { trackEvent } from 'helpers/analytics';
import { getSourcingAgentFormUrl } from '../../helpers/urlHelpers';
import usePageTitle from '../../hooks/usePageTitle';
import MainLayout from '../../components/layout/Main';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import { SourcingIllustration } from './components/sourcingIllustration';
import { AgentTitle } from './components/agentTitle';
import { AiGiftIcon } from './components/aiGiftIcon';
import { activeNetworkSelector, userSelector } from '../../redux/selectors';

const SourcingAgent = () => {
  usePageTitle('Sourcing Agent');
  const history = useHistory();
  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);

  return (
    <MainLayout>
      <MainLayoutHeader
        title={<AgentTitle />}
        rightActions={
          <Button
            variant="tertiary"
            icon={List}
            onClick={() => history.push(`/networks/${activeNetwork.slug}/sourcing-agent/campaigns`)}
          >
            View campaigns
          </Button>
        }
      />

      <Flex justifyContent="center" mt="100px" minHeight="calc(100vh - 200px)" p="24px">
        <Flex
          flexDirection="column"
          sx={{
            gap: '40px',
            maxWidth: '400px',
            width: '100%',
          }}
        >
          <Flex flexDirection="column" alignItems="center" sx={{ gap: '40px' }}>
            <SourcingIllustration />

            <Flex flexDirection="column" sx={{ gap: '16px', width: '100%' }}>
              <Heading fontSize="20px" fontWeight="semibold" color="text.dark" lineHeight="1.4">
                Get highly qualified applicants beyond your network
              </Heading>

              <Text fontSize="16px" color="text.main" lineHeight="1.4">
                Let the Sourcing AI Agent find and contact the right professionals for any role, delivering great
                candidates without the manual work.
              </Text>
            </Flex>
          </Flex>
          <Box
            sx={{
              bg: 'white',
              border: '1px solid transparent',
              borderRadius: '4px',
              p: '24px',
              backgroundImage:
                'linear-gradient(white, white), linear-gradient(94deg, #4900FF 3.21%, #D400FF 63.82%, #FF9257 134.26%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <Flex flexDirection="column" sx={{ gap: '24px' }}>
              <Flex alignItems="center">
                <Text flex="1" fontSize="16px" color="text.main" lineHeight="1.4">
                  Your{' '}
                  <Text as="span" fontWeight="semibold">
                    first 3 campaigns are free
                  </Text>{' '}
                  to explore what the Agent can do!
                </Text>

                <AiGiftIcon width="32px" height="32px" />
              </Flex>
              <Button
                variant="primary"
                size="large"
                width="100%"
                justifyContent="center"
                as="a"
                href={getSourcingAgentFormUrl({ email: user.email })}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onClick={() => {
                  trackEvent('sourcing_agent:create_campaign_external_form_click');
                }}
              >
                Start your first campaign
                <Box as={ExternalLink} width="16px" height="16px" aria-hidden="true" />
              </Button>
              <Text fontSize="14px" color="text.subtle" lineHeight="1.4">
                During this early beta, use the form linked above to start a new campaign.
              </Text>
            </Flex>
          </Box>
          <Flex flexDirection="column" sx={{ gap: '8px' }}>
            <Flex alignItems="flex-start" sx={{ gap: '8px' }}>
              <Box sx={{ flexShrink: 0, pt: '2px' }}>
                <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.300' }} aria-hidden="true" />
              </Box>
              <Text flex="1" fontSize="16px" color="text.main" lineHeight="1.4">
                Works for private and public jobs
              </Text>
            </Flex>
            <Flex alignItems="flex-start" sx={{ gap: '8px' }}>
              <Box sx={{ flexShrink: 0, pt: '2px' }}>
                <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.300' }} aria-hidden="true" />
              </Box>
              <Text flex="1" fontSize="16px" color="text.main" lineHeight="1.4">
                The Agent will not contact people at your network companies
              </Text>
            </Flex>
            <Flex alignItems="flex-start" sx={{ gap: '8px' }}>
              <Box sx={{ flexShrink: 0, pt: '2px' }}>
                <Box as={Check} width="16px" height="16px" sx={{ color: 'purple.300' }} aria-hidden="true" />
              </Box>
              <Text flex="1" fontSize="16px" color="text.main" lineHeight="1.4">
                Powered by{' '}
                <Text as="span" fontWeight="semibold">
                  Findem&apos;s 1B+ talent pool
                </Text>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </MainLayout>
  );
};

export default SourcingAgent;
